'use client';

import React, { useState } from 'react';

const tabs = ['START NEW', 'MARKET ME', 'FUND ME', 'PROTECT ME'] as const;
type Tab = (typeof tabs)[number];

type Card = {
  type: 'dashed' | 'blue' | 'orange' | 'white';
  badge?: { label: string; color: 'blue' | 'orange' };
  highlight?: string;
  title: string;
  subtitle?: string;
  pills?: string[];
  body?: string;
};

const data: Record<Tab, Card[]> = {
  'START NEW': [
    {
      type: 'dashed',
      badge: { label: 'BUSINESS ENTITY', color: 'blue' },
      title: 'Suitable Business Entity',
      pills: ['SOLE PROP.', 'NGO / NON-PROFIT', 'PVT LTD', 'LLP', 'PARTNERSHIP', 'OPC', 'MSME'],
    },
    {
      type: 'blue',
      subtitle: 'Registrations for all stages of',
      title: 'Your Business',
      pills: ['DPIIT', 'GST', 'MSME', 'TRADEMARK', 'FSSAI', 'IEC', 'ISO'],
    },
    {
      type: 'orange',
      subtitle: 'Our promise to you',
      title: '100% Transparent & Trackable Process',
    },
    {
      type: 'white',
      badge: { label: 'TRUSTED', color: 'orange' },
      highlight: '2000+',
      title: 'Trusted By Thousands',
      body: 'Startups & MSMEs Already Launched With Us',
    },
  ],
  'MARKET ME': [
    {
      type: 'dashed',
      badge: { label: 'ALWAYS ON', color: 'blue' },
      highlight: '24/7',
      title: 'Business Support',
      body: 'Round the clock assistance',
    },
    {
      type: 'blue',
      subtitle: 'Strategic',
      title: 'Crisis Planning',
      body: 'Risk & Recovery Solutions',
    },
    {
      type: 'orange',
      subtitle: 'Tailored solutions for',
      title: 'Business Continuity',
    },
    {
      type: 'white',
      badge: { label: 'SECURED', color: 'orange' },
      title: 'Assets & Data',
      body: 'Comprehensive protection for your digital and physical assets',
    },
  ],
  'FUND ME': [
    {
      type: 'dashed',
      badge: { label: 'GOVERNMENT', color: 'blue' },
      highlight: '50+',
      title: 'Funding Schemes',
      body: 'Government programs available',
    },
    {
      type: 'blue',
      subtitle: 'Funding schemes for',
      title: 'Manufacturing',
      body: 'Sector-specific support',
    },
    {
      type: 'orange',
      subtitle: 'Funding schemes for',
      title: 'Service Sector',
    },
    {
      type: 'white',
      badge: { label: 'AGRI', color: 'orange' },
      title: 'Agriculture Sector',
      body: 'Dedicated funding schemes for agri-businesses and farmers',
    },
  ],
  'PROTECT ME': [
    {
      type: 'dashed',
      badge: { label: 'SUPPORT', color: 'blue' },
      highlight: '24/7',
      title: 'Business Protection',
      body: 'Always available for you',
    },
    {
      type: 'blue',
      subtitle: 'Comprehensive coverage for',
      title: 'Risk Management',
    },
    {
      type: 'orange',
      subtitle: 'Security solutions for',
      title: 'Digital & Physical Assets',
    },
    {
      type: 'white',
      badge: { label: 'EXPERT', color: 'orange' },
      title: 'Crisis & Backup Plans',
      body: 'Expert advice on continuity and recovery strategies',
    },
  ],
};

function PulseDot({ color }: { color: 'blue' | 'orange' }) {
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full animate-pulse ${
        color === 'blue' ? 'bg-[#0060A9]' : 'bg-[#C16200]'
      }`}
    />
  );
}

function Badge({ label, color }: { label: string; color: 'blue' | 'orange' }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1 rounded-full mb-3 ${
        color === 'blue'
          ? 'bg-blue-50 text-[#0060A9]'
          : 'bg-orange-50 text-[#C16200]'
      }`}
    >
      <PulseDot color={color} />
      {label}
    </span>
  );
}

function CardContent({ card }: { card: Card }) {
  const isLight = card.type === 'dashed' || card.type === 'white';

  /* Pills layout */
  if (card.pills) {
    return (
      <div>
        {card.badge && <Badge label={card.badge.label} color={card.badge.color} />}
        {card.subtitle && (
          <p className={`text-xs font-medium mb-1 ${isLight ? 'text-slate-500' : 'text-white/70'}`}>
            {card.subtitle}
          </p>
        )}
        <h3 className={`text-base font-bold mb-3 ${isLight ? 'text-[#0060A9]' : 'text-white'}`}>
          {card.title}
        </h3>
        <div className="flex flex-wrap gap-2">
          {card.pills.map((p) => (
            <span
              key={p}
              className={`text-[10px] font-bold tracking-wide px-3 py-1 rounded-full border transition-colors ${
                isLight
                  ? 'border-blue-200 text-[#0060A9] bg-blue-50/60 hover:bg-blue-100'
                  : 'border-white/25 text-white bg-white/15 hover:bg-white/25'
              }`}
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    );
  }

  /* Highlight + title row (e.g. 24/7 Business Support) */
  if (card.highlight && card.type === 'dashed') {
    return (
      <div>
        {card.badge && <Badge label={card.badge.label} color={card.badge.color} />}
        <div className="flex items-center gap-4">
          <span className="text-5xl font-extrabold text-[#0060A9] leading-none">{card.highlight}</span>
          <div>
            <h3 className="text-base font-bold text-[#0060A9]">{card.title}</h3>
            {card.body && <p className="text-xs text-slate-500 mt-0.5">{card.body}</p>}
          </div>
        </div>
      </div>
    );
  }

  /* Highlight on white card (2000+) */
  if (card.highlight && card.type === 'white') {
    return (
      <div>
        {card.badge && <Badge label={card.badge.label} color={card.badge.color} />}
        <h3 className="text-sm font-semibold text-[#C16200] mb-2">{card.title}</h3>
        <div className="flex items-center gap-3">
          <span className="text-4xl font-extrabold text-slate-800 leading-none">{card.highlight}</span>
          {card.body && <p className="text-xs text-slate-500 leading-snug">{card.body}</p>}
        </div>
      </div>
    );
  }

  /* Standard layout */
  return (
    <div className={card.type === 'blue' || card.type === 'orange' ? 'text-center' : ''}>
      {card.badge && <Badge label={card.badge.label} color={card.badge.color} />}
      {card.subtitle && (
        <p className={`text-xs font-medium mb-1 ${isLight ? 'text-slate-500' : 'text-white/75'}`}>
          {card.subtitle}
        </p>
      )}
      <h3
        className={`font-bold leading-snug ${
          isLight ? 'text-[#0060A9]' : 'text-white'
        } ${card.title.length > 20 ? 'text-lg' : 'text-xl'}`}
      >
        {card.title}
      </h3>
      {card.body && (
        <p className={`text-xs mt-2 ${isLight ? 'text-slate-500' : 'text-white/70'}`}>{card.body}</p>
      )}
    </div>
  );
}

const cardBase =
  'relative rounded-3xl p-6 sm:p-7 flex flex-col justify-center min-h-[160px] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02]';

const cardVariants: Record<Card['type'], string> = {
  dashed:
    'bg-white/60 backdrop-blur-sm border-2 border-dashed border-blue-200 shadow-[0_8px_24px_rgba(0,96,169,0.08)]',
  blue: 'bg-gradient-to-br from-[#0060A9] to-[#0092D1] shadow-[0_12px_32px_rgba(0,96,169,0.32)]',
  orange: 'bg-gradient-to-br from-[#C16200] to-[#E8881A] shadow-[0_12px_32px_rgba(193,98,0,0.32)]',
  white: 'bg-white border border-blue-100 shadow-[0_10px_28px_rgba(0,0,0,0.07)]',
};

export function GuideMe() {
  const [activeTab, setActiveTab] = useState<Tab>('START NEW');
  const [animKey, setAnimKey] = useState(0);

  const handleTab = (tab: Tab) => {
    setActiveTab(tab);
    setAnimKey((k) => k + 1);
  };

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-[#e8f4ff] via-[#f0fbff] via-60% to-[#fff8ee] py-16 sm:py-10 lg:py-15">

      {/* Background blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(0,96,169,0.09)_0%,transparent_70%)] animate-[floatA_8s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(193,98,0,0.07)_0%,transparent_70%)] animate-[floatB_10s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(0,150,180,0.04)_0%,transparent_65%)]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-10 flex flex-col items-center text-center">
  
  <div className="mb-4 flex items-baseline gap-2 whitespace-nowrap">
    <span className="text-2xl md:text-3xl text-slate-400 font-normal italic">Guide me</span>
    <span className="text-4xl md:text-5xl font-bold tracking-tight text-[#C16200]">Far</span>
    <span className="text-4xl md:text-5xl font-bold tracking-tight text-[#0060A9]">Sight</span>
    <span className="ml-2 text-sm font-semibold text-[#0060A9] tracking-widest uppercase">Solutions</span>
  </div>

  {/* Tab bar */}
  <div className="mt-2 inline-flex flex-wrap justify-center gap-1 rounded-full border border-blue-100 bg-white/70 p-1.5 shadow-sm backdrop-blur-md">
    {tabs.map((tab) => (
      <button
        key={tab}
        onClick={() => handleTab(tab)}
        className={`rounded-full px-5 py-2.5 text-[11px] font-bold tracking-widest transition-all duration-300 whitespace-nowrap ${
          activeTab === tab
            ? 'bg-gradient-to-r from-[#0060A9] to-[#0092D1] text-white shadow-[0_4px_14px_rgba(0,96,169,0.35)] -translate-y-px'
            : 'text-slate-500 hover:bg-blue-50 hover:text-[#0060A9]'
        }`}
      >
        {tab}
      </button>
    ))}
  </div>
</div>

        {/* Cards grid */}
        <div
          key={animKey}
          className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6"
        >
          {data[activeTab].map((card, i) => (
            <div
              key={i}
              style={{ animationDelay: `${i * 70}ms` }}
              className={`${cardBase} ${cardVariants[card.type]} animate-[cardIn_0.45s_ease_forwards] opacity-0`}
            >
              <CardContent card={card} />
            </div>
          ))}
        </div>
      </div>

      {/* Keyframe styles */}
      <style>{`
        @keyframes floatA {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-22px) scale(1.05); }
        }
        @keyframes floatB {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(16px) scale(0.95); }
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </section>
  );
}

export default GuideMe;